import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Globe, Lock, Pencil, Users } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import {
  catalogService,
  teamService,
  type CatalogBindingView,
  type CatalogGrant,
  type CatalogGroups,
  type CatalogKind,
  type CatalogLevel,
  type CatalogResource,
  type CatalogVisibility,
} from '../services/api'
import { catalogOfferRows } from '../lib/catalogOffer'
import { formatSummary, grantCoversRole, summarizeVisibility, type BindingGrant, type VisibilitySummary } from './resourceVisibility'

const KIND_LABEL: Record<CatalogKind, string> = {
  document: 'Document',
  repository: 'Dépôt',
  cloud: 'Cloud',
  other: 'Autre',
}

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: { message?: string; usage?: Array<{ roleName: string; instanceName: string; agentName: string }> } } } }).response?.data
    const usage = data?.error?.usage
    if (usage && usage.length > 0) {
      const lines = usage.map((item) => `${item.roleName} — ${item.instanceName} (${item.agentName})`).join('\n')
      return `${data?.error?.message ?? 'Confirmation requise'}\n${lines}`
    }
    if (data?.error?.message) {
      return data.error.message
    }
  }
  return error instanceof Error ? error.message : 'La requête a échoué'
}

function usageLines(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const usage = (error as { response?: { data?: { error?: { usage?: Array<{ roleName: string; circleName: string; instanceName: string; agentName: string }> } } } }).response?.data?.error?.usage
    if (usage) {
      return usage.map((item) => `${item.roleName} (${item.circleName}) — instance ${item.instanceName}, agent ${item.agentName}`).join('\n')
    }
  }
  return ''
}

/**
 * * Resources attached to one team model or one team instance.
 * * A model save does not rewrite instances.
 */
export function TeamResourceBoard({
  mode,
  ownerId,
}: {
  mode: 'template' | 'instance'
  ownerId: string
}) {
  const queryClient = useQueryClient()
  const bindings = useQuery<CatalogBindingView>(['catalog-bindings', mode, ownerId], () => catalogService.bindings(mode, ownerId))
  const [kind, setKind] = useState<CatalogKind | ''>('')
  const [roleId, setRoleId] = useState('')
  const [text, setText] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const [showInstances, setShowInstances] = useState(false)
  const [hideAssociated, setHideAssociated] = useState(false)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const catalog = useQuery<CatalogGroups>('catalog-resources', () => catalogService.list())

  const rows = useMemo(() => groupRows(bindings.data, kind, roleId, text), [bindings.data, kind, roleId, text])
  const associatedIds = useMemo(
    () => new Set((bindings.data?.grants ?? []).map((grant) => grant.resourceId)),
    [bindings.data],
  )
  const offer = useMemo(
    () => catalogOfferRows(catalog.data, associatedIds, hideAssociated),
    [catalog.data, associatedIds, hideAssociated],
  )

  const save = async (resourceId: string, grants: BindingGrant[], confirmUsage = false) => {
    try {
      await catalogService.saveBindings(mode, ownerId, resourceId, grants.map((grant) => ({
        scope: grant.scope,
        level: grant.level,
        circleId: grant.circleId ?? null,
        roleId: grant.roleId ?? null,
      })), confirmUsage)
      setEditing(null)
      await queryClient.invalidateQueries(['catalog-bindings', mode, ownerId])
      if (mode === 'template') {
        setNotice('Les droits du modèle sont enregistrés. Les équipes déjà instanciées ne changent pas.')
        setShowInstances(true)
      } else {
        setNotice('Droits de l\'instance enregistrés.')
      }
    } catch (error) {
      const lines = usageLines(error)
      if (mode === 'instance' && lines && !confirmUsage) {
        const first = window.confirm(`Ce changement touche des agents en place :\n${lines}\n\nPremière confirmation ?`)
        const second = first && window.confirm(`Deuxième confirmation. Rôles et instances :\n${lines}`)
        if (second) {
          await save(resourceId, grants, true)
          return
        }
      }
      setShowInstances(false)
      setNotice(errorMessage(error))
    }
  }

  const setMembership = async (resource: CatalogResource, member: boolean) => {
    setPendingId(resource.id)
    try {
      if (member && !resource.picked) {
        await catalogService.pick(resource.id, true)
        await queryClient.invalidateQueries('catalog-resources')
      }
      await save(resource.id, member
        ? [{ scope: 'global', level: 'read', circleId: null, roleId: null }]
        : [])
    } catch (error) {
      setShowInstances(false)
      setNotice(errorMessage(error))
    } finally {
      setPendingId(null)
    }
  }

  return (
    <section className="card space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium text-primary">Ressources de l'équipe</h3>
      </div>
      {notice ? <p className={`text-sm whitespace-pre-wrap ${showInstances ? 'text-secondary' : 'text-error'}`}>{notice}</p> : null}
      {showInstances ? <TemplateInstanceLinks templateId={ownerId} /> : null}
      <div className="flex flex-wrap gap-2">
        <label className="text-sm text-secondary" htmlFor={`resource-filter-kind-${ownerId}`}>
          Type
          <select id={`resource-filter-kind-${ownerId}`} className="form-input mt-1" value={kind} onChange={(event) => setKind(event.target.value as CatalogKind | '')}>
            <option value="">Tous</option>
            {Object.entries(KIND_LABEL).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
        <label className="text-sm text-secondary" htmlFor={`resource-filter-role-${ownerId}`}>
          Rôle
          <select id={`resource-filter-role-${ownerId}`} className="form-input mt-1" value={roleId} onChange={(event) => setRoleId(event.target.value)}>
            <option value="">Tous</option>
            {(bindings.data?.roles ?? []).map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
          </select>
        </label>
        <label className="text-sm text-secondary" htmlFor={`resource-filter-text-${ownerId}`}>
          Nom
          <input id={`resource-filter-text-${ownerId}`} className="form-input mt-1" value={text} onChange={(event) => setText(event.target.value)} />
        </label>
      </div>
      <table className="w-full">
        <thead>
          <tr className="table-header">
            <th className="px-3 py-2 text-left text-xs">Nom</th>
            <th className="px-3 py-2 text-left text-xs">Type</th>
            <th className="px-3 py-2 text-left text-xs">Visibilité</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td className="px-3 py-3 text-sm text-secondary" colSpan={3}>Aucune ressource associée.</td></tr>
          ) : rows.map((row) => (
            <tr key={row.resourceId} className="table-row">
              <td className="px-3 py-2 text-sm text-primary">{row.name}</td>
              <td className="px-3 py-2 text-sm text-secondary">{KIND_LABEL[row.kind]}</td>
              <td className="px-3 py-2 text-sm text-secondary">
                <div className="flex items-start gap-2">
                  <VisibilityLines summary={row.visibility} />
                  <button type="button" className="action-button action-button--primary shrink-0" title="Modifier la visibilité" onClick={() => setEditing(row.resourceId)}>
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="space-y-2 border-t border-gray-200 pt-4">
        <h3 className="font-medium text-primary">Ressources disponibles</h3>
        <label className="flex items-center gap-2 text-sm text-secondary" htmlFor={`hide-associated-${ownerId}`}>
          <input
            id={`hide-associated-${ownerId}`}
            type="checkbox"
            checked={hideAssociated}
            onChange={(event) => setHideAssociated(event.target.checked)}
          />
          Masquer les ressources déjà associées
        </label>
        <table className="w-full">
          <thead>
            <tr className="table-header">
              <th className="px-3 py-2 text-left text-xs">Dans l'équipe</th>
              <th className="px-3 py-2 text-left text-xs">Nom</th>
              <th className="px-3 py-2 text-left text-xs">Type</th>
              <th className="px-3 py-2 text-left text-xs">Statut</th>
            </tr>
          </thead>
          <tbody>
            {catalog.isLoading ? (
              <tr><td className="px-3 py-3 text-sm text-secondary" colSpan={4}>Chargement des ressources…</td></tr>
            ) : offer.length === 0 ? (
              <tr><td className="px-3 py-3 text-sm text-secondary" colSpan={4}>{hideAssociated ? 'Aucune autre ressource.' : 'Aucune ressource disponible.'}</td></tr>
            ) : offer.map((resource) => (
              <tr key={resource.id} className="table-row">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label={`Dans l'équipe : ${resource.name}`}
                    checked={associatedIds.has(resource.id)}
                    disabled={pendingId !== null}
                    onChange={(event) => { void setMembership(resource, event.target.checked) }}
                  />
                </td>
                <td className="px-3 py-2 text-sm text-primary">{resource.name}</td>
                <td className="px-3 py-2 text-sm text-secondary">{KIND_LABEL[resource.kind]}</td>
                <td className="px-3 py-2 text-sm text-secondary">
                  <StatusMark visibility={resource.visibility} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && bindings.data ? (
        <AssociationDialog
          resourceId={editing}
          view={bindings.data}
          onClose={() => setEditing(null)}
          onSave={(grants) => void save(editing, grants)}
        />
      ) : null}
    </section>
  )
}

interface ResourceRow {
  resourceId: string
  name: string
  kind: CatalogKind
  visibility: VisibilitySummary
}

function groupRows(view: CatalogBindingView | undefined, kind: CatalogKind | '', roleId: string, text: string): ResourceRow[] {
  if (!view) {
    return []
  }
  const role = view.roles.find((item) => item.id === roleId)
  const byResource = new Map<string, CatalogGrant[]>()
  for (const grant of view.grants) {
    const list = byResource.get(grant.resourceId) ?? []
    list.push(grant)
    byResource.set(grant.resourceId, list)
  }
  const rows: ResourceRow[] = []
  for (const [resourceId, grants] of byResource) {
    const first = grants[0]
    if (!first) {
      continue
    }
    if (kind && first.kind !== kind) {
      continue
    }
    if (text && !first.name.toLowerCase().includes(text.trim().toLowerCase())) {
      continue
    }
    if (role && !grants.some((grant) => grantCoversRole(toGrant(grant), role.id, role.circleId))) {
      continue
    }
    rows.push({
      resourceId,
      name: first.name,
      kind: first.kind,
      visibility: summarizeVisibility(grants.map(toGrant), view.circles, view.roles),
    })
  }
  return rows
}

function toGrant(grant: CatalogGrant): BindingGrant {
  return { scope: grant.scope, level: grant.level, circleId: grant.circleId, roleId: grant.roleId }
}

function VisibilityLines({ summary }: { summary: VisibilitySummary }) {
  return (
    <span className="flex flex-col gap-0.5">
      {formatSummary(summary).split('\n').map((line) => {
        const cut = line.indexOf(' — ')
        if (cut < 0) {
          return <span key={line}>{line}</span>
        }
        return (
          <span key={line.slice(0, cut)}>
            <span className="font-medium text-primary">{line.slice(0, cut)}</span>
            {line.slice(cut)}
          </span>
        )
      })}
    </span>
  )
}

function TemplateInstanceLinks({ templateId }: { templateId: string }) {
  const instances = useQuery('teamInstances', () => teamService.getAllTeamInstances())
  const rows = (instances.data ?? []).filter((item) => item.teamTemplateId === templateId)
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-primary">Instances de cette équipe</p>
      {instances.isLoading ? <p className="text-sm text-secondary">Chargement des instances…</p> : null}
      {!instances.isLoading && rows.length === 0 ? (
        <p className="text-sm text-secondary">Aucune instance de cette équipe.</p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="text-sm">
          {rows.map((item) => (
            <li key={item.id}>
              <Link className="text-primary underline" to={`/instances/equipes#instance-${item.id}`}>
                {item.name?.trim() || 'Instance sans nom'}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function StatusMark({ visibility }: { visibility: CatalogVisibility }) {
  if (visibility === 'private') {
    return <span className="inline-flex text-secondary" role="img" aria-label="Privée" title="Privée"><Lock className="w-4 h-4" /></span>
  }
  if (visibility === 'public') {
    return <span className="inline-flex text-secondary" role="img" aria-label="Publique" title="Publique"><Globe className="w-4 h-4" /></span>
  }
  return <span className="inline-flex text-secondary" role="img" aria-label="Partagée" title="Partagée"><Users className="w-4 h-4" /></span>
}

function AssociationDialog({
  resourceId,
  view,
  onClose,
  onSave,
}: {
  resourceId: string
  view: CatalogBindingView
  onClose: () => void
  onSave: (grants: BindingGrant[]) => void
}) {
  const initial = view.grants.filter((grant) => grant.resourceId === resourceId).map(toGrant)
  const [globalLevel, setGlobalLevel] = useState<CatalogLevel | ''>(initial.find((grant) => grant.scope === 'global')?.level ?? '')
  const [circles, setCircles] = useState<Record<string, CatalogLevel | ''>>(() => Object.fromEntries(view.circles.map((circle) => {
    const found = initial.find((grant) => grant.scope === 'circle' && grant.circleId === circle.id)
    return [circle.id, found?.level ?? '']
  })))
  const [roles, setRoles] = useState<Record<string, CatalogLevel | ''>>(() => Object.fromEntries(view.roles.map((role) => {
    const found = initial.find((grant) => grant.scope === 'role' && grant.roleId === role.id)
    return [role.id, found?.level ?? '']
  })))
  const grants: BindingGrant[] = []
  if (globalLevel === 'read' || globalLevel === 'write') {
    grants.push({ scope: 'global', level: globalLevel, circleId: null, roleId: null })
  }
  for (const [circleId, level] of Object.entries(circles)) {
    if (level === 'read' || level === 'write') {
      grants.push({ scope: 'circle', level, circleId, roleId: null })
    }
  }
  for (const [id, level] of Object.entries(roles)) {
    if (level === 'read' || level === 'write') {
      grants.push({ scope: 'role', level, circleId: null, roleId: id })
    }
  }
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="studio-dialog bg-white rounded-lg p-4 space-y-3 max-h-[80vh] overflow-auto">
        <h3 className="font-semibold text-primary">Associer la ressource</h3>
        <p className="text-sm text-secondary">L'écriture inclut la lecture. Le modèle n'altère pas les instances déjà créées.</p>
        <label className="block text-sm text-secondary" htmlFor="grant-global">
          Globale (tous les rôles de l'équipe)
          <select id="grant-global" className="form-input mt-1" value={globalLevel} onChange={(event) => setGlobalLevel(event.target.value as CatalogLevel | '')}>
            <option value="">Pas d'association</option>
            <option value="read">Lecture</option>
            <option value="write">Écriture</option>
          </select>
        </label>
        <fieldset className="space-y-2">
          <legend className="text-sm text-secondary">Cercles</legend>
          {view.circles.map((circle) => (
            <label key={circle.id} className="flex items-center gap-2 text-sm text-primary" htmlFor={`grant-circle-${circle.id}`}>
              <span className="flex-1">{circle.name}</span>
              <select id={`grant-circle-${circle.id}`} className="form-input" value={circles[circle.id] ?? ''} onChange={(event) => setCircles({ ...circles, [circle.id]: event.target.value as CatalogLevel | '' })}>
                <option value="">Non</option>
                <option value="read">Lecture</option>
                <option value="write">Écriture</option>
              </select>
            </label>
          ))}
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="text-sm text-secondary">Rôles</legend>
          {view.roles.map((role) => (
            <label key={role.id} className="flex items-center gap-2 text-sm text-primary" htmlFor={`grant-role-${role.id}`}>
              <span className="flex-1">{role.name}</span>
              <select id={`grant-role-${role.id}`} className="form-input" value={roles[role.id] ?? ''} onChange={(event) => setRoles({ ...roles, [role.id]: event.target.value as CatalogLevel | '' })}>
                <option value="">Non</option>
                <option value="read">Lecture</option>
                <option value="write">Écriture</option>
              </select>
            </label>
          ))}
        </fieldset>
        <div className="flex gap-2 justify-end">
          <button type="button" className="button button--secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="button button--primary" onClick={() => onSave(grants)}>Enregistrer</button>
        </div>
      </div>
    </div>
  )
}

/**
 * * Same catalog, limited to the role open on the circle page.
 */
export function RoleCatalogResources({ teamId, circleId, roleId }: { teamId: string; circleId: string; roleId: string }) {
  const queryClient = useQueryClient()
  const bindings = useQuery<CatalogBindingView>(['catalog-bindings', 'template', teamId], () => catalogService.bindings('template', teamId), { enabled: teamId.length > 0 })
  const available = useQuery<CatalogGroups>('catalog-available', () => catalogService.available())
  const [resourceId, setResourceId] = useState('')
  const [level, setLevel] = useState<CatalogLevel>('read')
  const [notice, setNotice] = useState('')
  const covering = (bindings.data?.grants ?? []).filter((grant) => grantCoversRole(toGrant(grant), roleId, circleId))
  const choices = [
    ...(available.data?.mine ?? []),
    ...(available.data?.shared ?? []),
    ...(available.data?.public ?? []),
  ]
  const add = async () => {
    if (!resourceId || !bindings.data) {
      return
    }
    const existing = bindings.data.grants
      .filter((grant) => grant.resourceId === resourceId)
      .map(toGrant)
      .filter((grant) => !(grant.scope === 'role' && grant.roleId === roleId))
    try {
      await catalogService.saveBindings('template', teamId, resourceId, [
        ...existing.map((grant) => ({ scope: grant.scope, level: grant.level, circleId: grant.circleId ?? null, roleId: grant.roleId ?? null })),
        { scope: 'role', level, circleId: null, roleId },
      ])
      setNotice('Association enregistrée sur le modèle. Les instances déjà créées ne changent pas.')
      await queryClient.invalidateQueries(['catalog-bindings', 'template', teamId])
    } catch (error) {
      setNotice(errorMessage(error))
    }
  }
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-primary">Ressources</p>
      {covering.length === 0 ? <p className="text-xs text-secondary">Aucune ressource du catalogue pour ce rôle.</p> : covering.map((grant) => (
        <p key={`${grant.resourceId}-${grant.scope}-${grant.circleId ?? ''}-${grant.roleId ?? ''}`} className="text-xs text-secondary">
          {grant.name} · {grant.level === 'write' ? 'Écriture' : 'Lecture'} · {grant.scope === 'global' ? 'équipe' : grant.scope === 'circle' ? 'cercle' : 'rôle'}
        </p>
      ))}
      <div className="flex gap-2">
        <select id="role-catalog-resource" className="form-input" value={resourceId} onChange={(event) => setResourceId(event.target.value)} aria-label="Ressource à associer">
          <option value="">Choisir</option>
          {choices.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}
        </select>
        <select id="role-catalog-level" className="form-input" value={level} onChange={(event) => setLevel(event.target.value === 'write' ? 'write' : 'read')} aria-label="Droit">
          <option value="read">Lecture</option>
          <option value="write">Écriture</option>
        </select>
        <button type="button" className="button button--secondary" onClick={() => void add()}>Associer</button>
      </div>
      {notice ? <p className="text-xs text-secondary">{notice}</p> : null}
    </div>
  )
}
